import {
  Disclaimer,
  Div,
  EditPenIconRed,
  H5,
  Icon,
  P,
  PlusIcon,
  TaxRateFormModal,
  TaxRateRow,
  TrashIconRed,
  lightTheme,
} from '@vgrubs/components';
import {
  CreateTaxRateDto,
  TaxRateDraft,
} from 'components/src/types/taxRate.types';
import { Dispatch, SetStateAction, useState } from 'react';

type Props = {
  /** The AI lookup reads this location's address. Nothing else is fetched. */
  locationId: string | number;
  taxRates: TaxRateDraft[];
  setTaxRates: Dispatch<SetStateAction<TaxRateDraft[]>>;
};

/** Local only: a draft has no id until the location is verified. */
const createKey = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * The taxes the restaurant is verified with.
 *
 * A location can no longer be verified without them, so this section is open
 * rather than collapsed like the payouts above it - it is the one thing on
 * the form that has to be filled in by hand.
 *
 * Nothing here writes: the rates travel with the verify call and are stored in
 * the same transaction, so a verification that fails for some other reason
 * leaves nothing behind and can simply be tried again.
 */
export const VerificationTaxRates = ({
  locationId,
  taxRates,
  setTaxRates,
}: Props) => {
  const [formModal, setFormModal] = useState<TaxRateDraft | boolean | null>(
    null
  );

  const editing =
    typeof formModal === 'object' && formModal !== null ? formModal : null;

  const handleSaved = ({
    name,
    rate,
    type,
    applyToAllItems = true,
  }: CreateTaxRateDto) => {
    const values = { name, rate, type, applyToAllItems };

    setTaxRates((prev) =>
      editing
        ? prev.map((taxRate) =>
            taxRate.key === editing.key
              ? { ...values, key: editing.key }
              : taxRate
          )
        : [...prev, { ...values, key: createKey() }]
    );
    setFormModal(null);
  };

  return (
    <Div $box $flex $column $p="24px" $width="100%" $mt="24">
      <Div $flex $alignCenter $between $mb={taxRates.length ? '16' : '0'}>
        <H5 $mb="0">Tax Rates</H5>
        <Icon $pointer onClick={() => setFormModal(true)}>
          <PlusIcon />
        </Icon>
      </Div>

      {taxRates.length === 0 ? (
        <P $m="16px 0" $center>
          No taxes yet.
        </P>
      ) : (
        taxRates.map((taxRate) => (
          <TaxRateRow
            key={taxRate.key}
            taxRate={taxRate}
            actions={
              <>
                <Icon
                  $shadow
                  $pointer
                  $size="32"
                  onClick={() => setFormModal(taxRate)}
                >
                  <EditPenIconRed />
                </Icon>
                <Icon
                  $pointer
                  onClick={() =>
                    setTaxRates((prev) =>
                      prev.filter((rate) => rate.key !== taxRate.key)
                    )
                  }
                >
                  <TrashIconRed />
                </Icon>
              </>
            }
          />
        ))
      )}

      <Disclaimer $m="8px 0 0" $left $color={lightTheme.darkGray}>
        A restaurant cannot be verified without taxes, and at least one of them
        has to apply to all items - that is the one charged on every menu item
        with no tax of its own. They are saved together with the verification.
      </Disclaimer>

      <TaxRateFormModal
        formModal={formModal}
        closeModal={() => setFormModal(null)}
        locationId={locationId}
        onDraftSaved={handleSaved}
      />
    </Div>
  );
};
